/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PickupGroupsPage.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Main page for Pickup Groups management with list view - simplified using hooks.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneOff, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { deletePickupGroupById, navigateToPickupGroupDuplicate } from './pickupGroupsActions';
import { usePickupGroupsList } from './hooks/usePickupGroupsList';
import { deletePickupGroup } from './api/pickupGroupsApi';
import type { PickupGroup } from './types';

export default function PickupGroupsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; group: PickupGroup | null }>({
    isOpen: false,
    group: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = usePickupGroupsList({
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    domainId: selectedDomain?.id,
  });

  const handleCreate = useCallback(() => {
    navigate('/pbx/applications/pickup-groups/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (group: PickupGroup) => {
      navigate(`/pbx/applications/pickup-groups/${group.uuid || group.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (group: PickupGroup) => {
      navigateToPickupGroupDuplicate(navigate, group.uuid || group.id);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((group: PickupGroup) => {
    setDeleteModal({ isOpen: true, group });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.group) return;

    setIsDeleting(true);
    try {
      await deletePickupGroup(String(deleteModal.group.uuid || deleteModal.group.id));

      showSuccess(t('pickupGroups.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, group: null });
      await refresh();
    } catch (error) {
      showError(error?.message || t('pickupGroups.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.group, refresh, showError, showSuccess, t]);

  const renderRowActions = useCallback(
    (group: PickupGroup) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            onClick={() => handleEdit(group)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.duplicate')} position="top">
          <button
            onClick={() => handleDuplicate(group)}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            onClick={() => handleDeleteClick(group)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDeleteClick}
          onColumnToggle={handleColumnToggle}
          title={t('pickupGroups.title')}
          titleIcon={<PhoneOff className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          renderActions={renderRowActions}
          emptyMessage={t('pickupGroups.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, group: null })}
        onConfirm={handleDeleteConfirm}
        title={t('pickupGroups.deleteTitle')}
        message={t('pickupGroups.messages.confirmDelete', {
          name: deleteModal.group?.name || ''
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

